import type { GenerateBuildingFeatureSet } from "building-types/building/buildingTypes";
import type { Doc } from "building-types/doc";
import type { DocEntityId } from "building-types/doc/docEntityId";
import type { IsolatableEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { ParameterValue } from "building-types/options/parameter";
import { ActiveOptionStateParameterValue } from "building-types/systemParameterTypes";
import type { SheetParameterValues } from "building-types/systemParameterTypes";
import type {
  EvaluatedDataTable,
  EvaluatedViewport,
  ViewportRenderStyle,
  ViewTemplate,
} from "building-types/views/planSets";
import type { Table } from "common/data/table";
import type { PlanGeometryGenerationDiagnostic } from "doc/plans/planGeometrySourceTypes";
import type { Mat3 } from "geom/mat3";
import type { EditorDebugPrefs } from "lib/studio/editorTypes";
import type { Group } from "three";

export interface ExplicitViewportInputs<
  TViewport extends EvaluatedViewport = EvaluatedViewport,
  TViewTemplate extends ViewTemplate = ViewTemplate,
> {
  readonly doc: Doc;
  readonly viewport: TViewport;
  readonly viewTemplate: TViewTemplate;
  readonly optionChoices: OptionChoices | null;
  // Keep transient renderer modes out of the persisted Viewport/document schema.
  readonly renderStyleMode?: ViewportRenderStyle;
  readonly enabledFeatures?: GenerateBuildingFeatureSet;
  readonly isolatedEntity?: IsolatableEntityHandle;
}

export interface ExplicitPlanViewportInputs<
  TViewport extends EvaluatedViewport = EvaluatedViewport,
  TViewTemplate extends ViewTemplate = ViewTemplate,
> extends ExplicitViewportInputs<TViewport, TViewTemplate> {
  readonly globalParameterValues: Table<ParameterValue>;
  readonly debugPrefs?: EditorDebugPrefs;
  readonly planSetTemplateId?: DocEntityId<"PLAN_SET_TEMPLATE">;
  readonly planSetTemplateConfigId?: DocEntityId<"PLAN_SET_TEMPLATE_CONFIG">;
}

export interface ExplicitViewportRendererInputs<
  TViewport extends EvaluatedViewport = EvaluatedViewport,
  TViewTemplate extends ViewTemplate = ViewTemplate,
> {
  readonly viewport: TViewport;
  readonly viewTemplate: TViewTemplate;
  readonly optionChoices: OptionChoices | null;
  readonly sheetParameterValues?: Partial<SheetParameterValues>;
  readonly renderOrder: number;
  readonly transform: Mat3;
  readonly geometryCullingEnabled?: boolean;
  readonly onGroupRendered?: (group: Group, viewport: TViewport) => void;
  readonly collectedPromises?: Promise<unknown>[];
  readonly onError?: (error: Error) => void;
  readonly onDiagnostic?: (diagnostic: PlanGeometryGenerationDiagnostic) => void;
  // Keep transient renderer modes out of the persisted Viewport/document schema.
  readonly renderStyleMode?: ViewportRenderStyle;
}

export function resolveOptionChoices(doc: Doc, entity: EvaluatedViewport | EvaluatedDataTable): OptionChoices | null {
  if (entity.entityType === "VIEWPORT" && entity.viewType === "DRAWING") {
    return {};
  }

  if (!entity.presetId) {
    return null;
  }

  const presetOptionChoices =
    entity.presetId === ActiveOptionStateParameterValue
      ? doc.optionChoices
      : doc.presets[entity.presetId]?.optionChoices;

  if (!presetOptionChoices) {
    return null;
  }

  return {
    ...presetOptionChoices,
    ...entity.optionSetOverrides,
  };
}
